import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Alert,
  Box,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import StorageIcon from "@mui/icons-material/Storage";
import BarChartIcon from "@mui/icons-material/BarChart";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SellIcon from "@mui/icons-material/Sell";

const OverviewTab = () => (
  <Card>
    <CardContent>
      <Typography variant="h5" gutterBottom>
        📊 Statistics & Data Pipeline
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        This module connects your purchase and sales documents (e.g. Google
        Sheets shopping tables) to a structured database pipeline and surfaces
        the data through <strong>Metabase</strong> dashboards — giving you live
        insights into buying activity, stock costs, and sales performance
        without manual reporting.
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: "center" }}>
              <Box display="flex" justifyContent="center" gap={1} mb={1}>
                <ShoppingCartIcon color="primary" sx={{ fontSize: 36 }} />
                <SellIcon color="secondary" sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="h6">Source Documents</Typography>
              <Typography variant="body2" color="text.secondary">
                Purchase orders and sales records are maintained in Google
                Sheets. Each row represents a transaction — articles, prices,
                quantities, ASINs, and delivery status are tracked per entry.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: "center" }}>
              <Box display="flex" justifyContent="center" gap={1} mb={1}>
                <ScheduleIcon color="success" sx={{ fontSize: 36 }} />
                <StorageIcon color="success" sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="h6">Scheduled Import</Typography>
              <Typography variant="body2" color="text.secondary">
                A configurable cron job reads the Google Sheet on a schedule
                (e.g. nightly at 03:00), parses every row, deduplicates by date
                + ASIN/SKU, and upserts records into the local PostgreSQL
                database. You can also trigger a manual import at any time.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: "center" }}>
              <BarChartIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6">Metabase Dashboards</Typography>
              <Typography variant="body2" color="text.secondary">
                Once data is in the database, Metabase connects directly and
                lets you build charts, pivot tables, and KPI cards — purchase
                costs over time, margin analysis per article, arrived vs. booked
                quantities, and much more.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" gutterBottom>
        🚀 How to set up the pipeline
      </Typography>
      <ol style={{ paddingLeft: "20px" }}>
        <li style={{ marginBottom: 8 }}>
          <strong>Sheet Settings tab:</strong> Paste your Google Sheets URL (or
          enter IDs manually) and map each column letter to the corresponding
          field (e.g. <code>datum → A</code>, <code>asin_sku → L</code>). Save
          the settings.
        </li>
        <li style={{ marginBottom: 8 }}>
          <strong>Shopping Table tab:</strong> Use the cron scheduler to define
          how often data should be imported, then start the schedule. Use
          <em> Run Import Now</em> for an immediate one-off sync.
        </li>
        <li style={{ marginBottom: 8 }}>
          <strong>Metabase tab:</strong> Opens your Metabase instance in a new
          browser tab. Create questions and dashboards that query the{" "}
          <code>shopping_table</code> table directly.
        </li>
      </ol>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>💡 Tip:</strong> The import is idempotent — re-running it
          won't create duplicate rows. Existing records matched by{" "}
          <em>date + ASIN/SKU</em> are updated in place, so you can safely run
          it as often as needed.
        </Typography>
      </Alert>
    </CardContent>
  </Card>
);

export default OverviewTab;
